import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Loader2,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Search,
  CalendarCheck,
  PawPrint,
  LayoutDashboard,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import TurnstileWidget from '../components/TurnstileWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The 10 animal types from the user spec (matches backend AnimalType enum).
const ANIMAL_TYPES = [
  { value: 'HORSE',   label: 'Horse' },
  { value: 'COW',     label: 'Cow' },
  { value: 'SHEEP',   label: 'Sheep' },
  { value: 'GOAT',    label: 'Goat' },
  { value: 'PIG',     label: 'Pig' },
  { value: 'CHICKEN', label: 'Chicken' },
  { value: 'DOG',     label: 'Dog' },
  { value: 'CAMEL',   label: 'Camel' },
  { value: 'EXOTIC',  label: 'Exotic' },
  { value: 'OTHER',   label: 'Other' },
];

/**
 * Public Client Onboarding — 4-step wizard. Anyone can sign up; no invite,
 * promo code, or approval. Client accounts are always free.
 *
 * Step 1: Basic account (required)
 * Step 2: Location (optional — skip allowed)
 * Step 3: Animals (optional — skip allowed; can be added later)
 * Step 4: Welcome screen with 4 CTAs
 */
export default function ClientSignupPage() {
  const navigate = useNavigate();
  const { login, token } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);

  const [basic, setBasic] = useState({ name: '', email: '', phone: '', password: '' });
  const [location, setLocation] = useState({ address: '', city: '', state: '', zip: '' });
  const [animals, setAnimals] = useState([]); // {tmpId, name, animalType, notes}

  const setField = (setter, key) => (e) => setter((s) => ({ ...s, [key]: e.target.value }));

  // ---------- Step 1: Create the account (requires CAPTCHA) ----------
  const submitStep1 = async (e) => {
    e?.preventDefault();
    const { name, email, password } = basic;
    if (!name.trim() || !email.trim() || !password) {
      toast.error('Please fill in name, email, and password');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!turnstileToken) {
      toast.error('Please complete the CAPTCHA verification');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        name: basic.name.trim(),
        email: basic.email.trim(),
        phone: basic.phone.trim() || null,
        password: basic.password,
      };
      const res = await axios.post(`${API_URL}/api/auth/signup-client`, payload, {
        headers: { 'X-Turnstile-Token': turnstileToken },
      });
      await login(res.data.token);
      toast.success('Account created — welcome!');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign up failed.');
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Step 2: Save location to the client profile ----------
  const saveLocation = async () => {
    // All fields optional — if everything blank, skip the network call
    const payload = {
      address: location.address.trim() || null,
      city:    location.city.trim()    || null,
      state:   location.state.trim()   || null,
      zip:     location.zip.trim()     || null,
    };
    const hasAny = Object.values(payload).some((v) => v);
    if (!hasAny) {
      setStep(3);
      return;
    }
    try {
      setLoading(true);
      await axios.patch(`${API_URL}/api/client/me`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStep(3);
    } catch (err) {
      // Non-blocking — let the user continue even if save fails
      toast.message("Couldn't save location now — you can add it later in settings");
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Step 3: Add animals (optional) ----------
  const addAnimalRow = () => {
    setAnimals((a) => [
      ...a,
      { tmpId: `t_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, name: '', animalType: 'HORSE', notes: '' },
    ]);
  };

  const updateAnimal = (tmpId, patch) => {
    setAnimals((a) => a.map((it) => (it.tmpId === tmpId ? { ...it, ...patch } : it)));
  };

  const removeAnimal = (tmpId) => {
    setAnimals((a) => a.filter((it) => it.tmpId !== tmpId));
  };

  const saveAnimalsAndFinish = async () => {
    const valid = animals.filter((a) => a.name.trim());
    if (valid.length === 0) {
      setStep(4);
      return;
    }
    try {
      setLoading(true);
      for (const a of valid) {
        try {
          await axios.post(
            `${API_URL}/api/client/animals`,
            {
              name: a.name.trim(),
              animalType: a.animalType,
              notes: a.notes.trim() || null,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (e) {
          // Continue with remaining animals on individual failures
        }
      }
      toast.success(`${valid.length} animal${valid.length === 1 ? '' : 's'} added`);
      setStep(4);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Render ----------
  return (
    <div className="min-h-screen flex flex-col items-center bg-background px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-6">
          <Logo variant="full" size="md" />
        </div>

        {/* Step indicator */}
        {step < 4 && (
          <div className="flex items-center justify-between mb-4 px-1" data-testid="signup-stepper">
            {[1, 2, 3].map((n, i) => (
              <React.Fragment key={n}>
                <div
                  className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    step >= n
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {step > n ? <CheckCircle2 className="h-4 w-4" /> : n}
                </div>
                {i < 2 && (
                  <div className={`flex-1 h-px mx-2 ${step > n ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {step === 1 && (
          <Card data-testid="signup-step-1">
            <CardHeader>
              <CardTitle className="text-2xl">Create your client account</CardTitle>
              <CardDescription>
                Free forever — book ranch services, track appointments, and save your animals all in
                one place.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitStep1} className="space-y-4">
                <Field id="name" label="Full Name" testid="client-signup-name" value={basic.name} onChange={setField(setBasic, 'name')} placeholder="Jane Rancher" autoComplete="name" />
                <Field id="email" label="Email" testid="client-signup-email" type="email" value={basic.email} onChange={setField(setBasic, 'email')} placeholder="you@example.com" autoComplete="email" />
                <Field id="phone" label="Phone (optional)" testid="client-signup-phone" type="tel" value={basic.phone} onChange={setField(setBasic, 'phone')} placeholder="(555) 555-5555" autoComplete="tel" hint="Helps link prior bookings made through providers." />
                <Field id="password" label="Password" testid="client-signup-password" type="password" value={basic.password} onChange={setField(setBasic, 'password')} placeholder="At least 8 characters" autoComplete="new-password" />

                <div className="flex justify-center">
                  <TurnstileWidget onToken={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 font-semibold"
                  disabled={loading || !turnstileToken}
                  data-testid="client-signup-submit"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Creating account…</>
                  ) : (
                    <>Continue <ArrowRight className="ml-2 h-4 w-4" /></>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <button type="button" onClick={() => navigate('/login')} className="text-primary hover:underline font-medium" data-testid="link-client-login">
                  Sign in
                </button>
              </div>

              <BackHomeLink onClick={() => navigate('/')} />
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card data-testid="signup-step-2">
            <CardHeader>
              <CardTitle className="text-2xl">Where are you located?</CardTitle>
              <CardDescription>
                Optional. Lets providers offering mobile service know they can serve you.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field id="address" label="Street address" testid="signup-address" value={location.address} onChange={setField(setLocation, 'address')} placeholder="1234 Pasture Rd" />
              <div className="grid grid-cols-2 gap-3">
                <Field id="city" label="City" testid="signup-city" value={location.city} onChange={setField(setLocation, 'city')} placeholder="Bonanza" />
                <Field id="state" label="State" testid="signup-state" value={location.state} onChange={setField(setLocation, 'state')} placeholder="OR" />
              </div>
              <Field id="zip" label="ZIP" testid="signup-zip" value={location.zip} onChange={setField(setLocation, 'zip')} placeholder="97623" />

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setStep(3)} className="flex-1" data-testid="signup-skip-location">
                  Skip
                </Button>
                <Button onClick={saveLocation} disabled={loading} className="flex-1" data-testid="signup-save-location">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="ml-1 h-4 w-4" /></>}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card data-testid="signup-step-3">
            <CardHeader>
              <CardTitle className="text-2xl">Add your animals</CardTitle>
              <CardDescription>
                Optional. Add as many as you like — you can always add more later from your dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {animals.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No animals added yet.
                </p>
              )}

              {animals.map((a) => (
                <AnimalRow
                  key={a.tmpId}
                  animal={a}
                  onChange={(patch) => updateAnimal(a.tmpId, patch)}
                  onRemove={() => removeAnimal(a.tmpId)}
                />
              ))}

              <Button type="button" variant="outline" onClick={addAnimalRow} className="w-full" data-testid="signup-add-animal">
                + Add an animal
              </Button>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setStep(4)} className="flex-1" data-testid="signup-skip-animals">
                  Skip for now
                </Button>
                <Button onClick={saveAnimalsAndFinish} disabled={loading} className="flex-1" data-testid="signup-save-animals">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Finish <ArrowRight className="ml-1 h-4 w-4" /></>}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 4 && (
          <WelcomeStep navigate={navigate} />
        )}
      </div>
    </div>
  );
}

// ----------------------------- Sub-components -----------------------------

function Field({ id, label, hint, testid, type = 'text', value, onChange, placeholder, autoComplete }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function BackHomeLink({ onClick }) {
  return (
    <div className="mt-4 text-center">
      <button type="button" onClick={onClick} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center">
        <ArrowLeft className="mr-1 h-4 w-4" /> Back to Home
      </button>
    </div>
  );
}

function AnimalRow({ animal, onChange, onRemove }) {
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid={`animal-row-${animal.tmpId}`}>
      <div className="grid grid-cols-2 gap-2">
        <Input
          value={animal.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Animal name"
          data-testid={`animal-name-${animal.tmpId}`}
        />
        <select
          value={animal.animalType}
          onChange={(e) => onChange({ animalType: e.target.value })}
          className="border rounded-md px-3 text-sm bg-background"
          data-testid={`animal-type-${animal.tmpId}`}
        >
          {ANIMAL_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>
      <Input
        value={animal.notes}
        onChange={(e) => onChange({ notes: e.target.value })}
        placeholder="Quick notes (optional)"
        data-testid={`animal-notes-${animal.tmpId}`}
      />
      <button
        type="button"
        onClick={onRemove}
        className="text-xs text-muted-foreground hover:text-red-600"
      >
        Remove
      </button>
    </div>
  );
}

function WelcomeStep({ navigate }) {
  const tiles = [
    { icon: Search,         label: 'View Marketplace', desc: 'Browse local providers', to: '/' },
    { icon: CalendarCheck,  label: 'Book a Provider',  desc: 'Schedule your first service', to: '/' },
    { icon: PawPrint,       label: 'Add Animals',      desc: 'Build your animal profile', to: '/client/dashboard?tab=animals' },
    { icon: LayoutDashboard, label: 'Go to Dashboard', desc: 'See your account', to: '/client/dashboard' },
  ];

  return (
    <Card data-testid="signup-step-4">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-2">
          <div className="h-14 w-14 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <CardTitle className="text-2xl">You're all set!</CardTitle>
        <CardDescription>What would you like to do next?</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3">
          {tiles.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.label}
                type="button"
                onClick={() => navigate(t.to)}
                className="text-left rounded-lg border p-4 hover:bg-muted hover:border-primary/40 transition-colors"
                data-testid={`welcome-tile-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="h-6 w-6 text-primary mb-2" />
                <p className="font-semibold text-sm">{t.label}</p>
                <p className="text-xs text-muted-foreground">{t.desc}</p>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
